import { useEffect, useMemo, useState } from "react";
import { CustomTable } from "../../componentes/table/CustomTable";
import DateMasked from "../../componentes/inputMask/DateMasked";
import Modal from "../../componentes/modal/modal";
import { formatLocalDate, formatLocalDateTime } from "../../utils/formatDate";
import Input from "../../componentes/input/input";
import { Checkbox } from "primereact/checkbox";
import GreenmileService from "../../services/resource/GreenmileService";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import DropDown from "../../componentes/dropdown/dropdown";

const inputClassName = "w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-colors duration-150 placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/15";

const formatDate = (value) => value ? formatLocalDate(value) : "—";

function toIsoDate(value) {
    if (!value || typeof value !== "string") return "";
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
}

function toDateTimeLocal(value) {
    if (!value || typeof value !== "string") return "";
    const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    return match ? match[1] : "";
}

export default function RotaGreenmilePage() {
    const service = useMemo(() => new GreenmileService(), []);

    const [lista, setLista] = useState([]);
    const [registro, setRegistro] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState(() => {
        const hoje = new Date();
        const dataInicial = new Date();
        dataInicial.setDate(hoje.getDate() - 30);

        const formatar = (data) => data.toISOString().split("T")[0];

        return {
            dataInicial: formatar(dataInicial),
            dataFinal: formatar(hoje),
            transporte: ""
        };
    });

    async function buscar(e) {
        e.preventDefault();
        carregarRotas();
    }

    async function carregarRotas() {
        setLoading(true);

        try {
            const response = await service.listarRotas(filtro);
            setLista(response.data ?? []);
        } finally {
            setLoading(false);
        }
    }

    async function salvar() {
        if (!registro) return;

        setLoading(true);
        try {
            await service.editar(registro);
            setRegistro(null);
            carregarRotas();
        } finally {
            setLoading(false);
        }
    }

    function selecionar(item) {
        setRegistro({ ...item });
    }

    function alterarCampo(campo, valor) {
        setRegistro((atual) => ({ ...atual, [campo]: valor }));
    }

    const columns = useMemo(() => [
        {
            key: "filialNome",
            label: "Filial",
            sortable: true,
        },
        {
            key: "data",
            label: "Data",
            sortable: true,
            render: (value) => formatDate(value),
        },
        { key: "transporte", label: "Transporte", sortable: true },
        { key: "carga", label: "Carga", sortable: true, className: "font-medium" },
        {
            key: "imagens",
            label: "Imagens",
            sortable: true,
        },
        {
            key: "saidaRealizada",
            label: "Hora Saída",
            sortable: true,
            render: (value) => formatLocalDateTime(value)

        },
        {
            key: "chegadaRealizada",
            label: "Hora Chegada",
            sortable: true,
            render: (value) => formatLocalDateTime(value)
        },
        {
            key: "entregueNoPrazo",
            label: "No Prazo",
            sortable: true,
        },
        {
            key: "editadoManualmente",
            label: "Bloqueado",
            sortable: true,
            render: (value) => (
                <div>
                    {value === true && <span className="bg-amber-500/10 text-amber-700 px-2 py-1 rounded-full">Bloqueado</span>}
                </div>
            ),
        },
        {
            key: "acoes",
            label: "Ações",
            sortable: false,
            filterable: false,
            noSelected: true,
            render: (_, item) => (
                <button
                    type="button"
                    onClick={() => selecionar(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-[10px] font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                    <i className="fa fa-pencil" /> Editar
                </button>
            ),
        }
    ], []);

    return (
        <div>
            <main className="space-y-4 pt-3">
                <section>
                    <form onSubmit={buscar} className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2 xl:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(180px,1fr)_auto] xl:items-end">
                        <DateMasked
                            id="data-inicial"
                            label="Data inicial"
                            value={filtro.dataInicial}
                            onChange={(dataInicial) => setFiltro((atual) => ({ ...atual, dataInicial }))}
                        />
                        <DateMasked
                            id="data-final"
                            label="Data final"
                            value={filtro.dataFinal}
                            onChange={(dataFinal) => setFiltro((atual) => ({ ...atual, dataFinal }))}
                        />
                        <Input
                            className={inputClassName}
                            label="Transporte"
                            value={filtro.transporte}
                            onChange={(event) => setFiltro((atual) => ({ ...atual, transporte: event.target.value }))}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-xs transition-all duration-150 hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-search"} /> Buscar
                        </button>
                    </form>

                    <CustomTable
                        data={lista}
                        columns={columns}
                        loading={loading}
                        pagination
                        pageSize={20}
                        exportable
                        showHeader={false}
                        exportFilename="Rota Greenmile"
                        className="text-[11px]"
                    />
                </section>

                <Modal
                    isOpen={Boolean(registro)}
                    onClose={() => setRegistro(null)}
                    title="Editar rota"
                    subtitle="Altere os campos necessários e salve as atualizações."
                    className="w-[92%]"
                    footer={
                        <div className="flex items-center justify-between border-t border-border px-4 py-3">

                            <div className="flex gap-1">

                                <Checkbox
                                    inputId="editadoManualmente"
                                    checked={registro?.editadoManualmente}
                                    onChange={(e) => alterarCampo("editadoManualmente", e.checked)}
                                />
                                <label htmlFor="editadoManualmente" className="mr-5">
                                    Bloqueia da importação
                                </label>

                                <Checkbox
                                    inputId="cargaVinculada"
                                    disabled
                                    checked={registro?.cargaVinculada}
                                    onChange={(e) => alterarCampo("cargaVinculada", e.checked)}
                                />
                                <label htmlFor="cargaVinculada">
                                    Carga vinculada
                                </label>

                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setRegistro(null)}
                                    className="rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150"
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    onClick={salvar}
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 rounded-lg bg-primary hover:brightness-95 px-4 py-2 text-sm font-semibold text-primary-foreground transition-all duration-150"
                                >
                                    <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-save"} />
                                    Salvar alterações
                                </button>
                            </div>

                        </div>
                    }
                >
                    {registro && (
                        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">

                            <Input
                                label="Filial"
                                value={registro.filialNome ?? ""}
                                disabled
                            />

                            <Input
                                label="Transporte"
                                value={registro.transporte ?? ""}
                                onChange={(e) => alterarCampo("transporte", e.target.value)}
                            />

                            <Input
                                label="Carga"
                                value={registro.carga ?? ""}
                                onChange={(e) => alterarCampo("carga", e.target.value)}
                            />

                            <Input
                                label="Descrição da Carga"
                                value={registro.descricaoCarga ?? ""}
                                onChange={(e) => alterarCampo("descricaoCarga", e.target.value)}
                            />

                            <DateMasked
                                label="Data"
                                value={toIsoDate(registro.data)}
                                onChange={(v) => alterarCampo("data", v)}
                            />

                            <Input
                                type="datetime-local"
                                label="Saída Planejada Original"
                                disabled
                                value={toDateTimeLocal(registro.saidaPlanejadaOriginal)}
                                onChange={(e) => alterarCampo("saidaPlanejadaOriginal", e.target.value)}
                            />

                            <Input
                                type="datetime-local"
                                label="Saída Realizada"
                                value={toDateTimeLocal(registro.saidaRealizada)}
                                onChange={(e) => alterarCampo("saidaRealizada", e.target.value)}
                            />


                            <Input
                                type="datetime-local"
                                label="Chegada Planejada Original"
                                disabled
                                value={toDateTimeLocal(registro.chegadaPlanejadaOriginal)}
                                onChange={(e) => alterarCampo("chegadaPlanejadaOriginal", e.target.value)}
                            />

                            <Input
                                type="datetime-local"
                                label="Chegada Realizada"
                                value={toDateTimeLocal(registro.chegadaRealizada)}
                                onChange={(e) => alterarCampo("chegadaRealizada", e.target.value)}
                            />

                                                <DropDown
                                label="Entregue no Prazo"
                                options={[
                                    { label: 'No Prazo', value: 'NO_PRAZO' },
                                    { label: 'Fora do Prazo', value: 'FORA_DO_PRAZO' },
                                    { label: 'Indefinido', value: 'INDEFINIDO' },
                                ]}
                                filter={false}
                                value={registro.entregueNoPrazo ?? ""}
                                onChange={(e) => alterarCampo("entregueNoPrazo", e.target.value)}
                            />


                            <Input
                                label="Distância Planejada"
                                type="number"
                                value={registro.distanciaPlanejada ?? ""}
                                onChange={(e) => alterarCampo("distanciaPlanejada", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Distância Planejada Original"
                                type="number"
                                value={registro.distanciaPlanejadaOriginal ?? ""}
                                onChange={(e) => alterarCampo("distanciaPlanejadaOriginal", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Distância Realizada"
                                type="number"
                                value={registro.distanciaRealizada ?? ""}
                                onChange={(e) => alterarCampo("distanciaRealizada", e.target.value)}
                            />

                            <Input
                                type="datetime-local"
                                label="Finalização Planejada Original"
                                disabled
                                value={toDateTimeLocal(registro.finalizacaoPlanejadaOriginal)}
                                onChange={(e) => alterarCampo("finalizacaoPlanejadaOriginal", e.target.value)}
                            />

                            <Input
                                type="datetime-local"
                                disabled
                                label="Início Planejado"
                                value={toDateTimeLocal(registro.inicioPlanejado)}
                                onChange={(e) => alterarCampo("inicioPlanejado", e.target.value)}
                            />

                            <Input
                                type="datetime-local"
                                label="Início Planejado Original"
                                disabled
                                value={toDateTimeLocal(registro.inicioPlanejadoOriginal)}
                                onChange={(e) => alterarCampo("inicioPlanejadoOriginal", e.target.value)}
                            />

                            <Input
                                label="Código Motorista"
                                value={registro.codigoMotorista ?? ""}
                                onChange={(e) => alterarCampo("codigoMotorista", e.target.value)}
                            />

                            <Input
                                label="Volume Planejado"
                                type="number"
                                value={registro.volumePlanejado ?? ""}
                                onChange={(e) => alterarCampo("volumePlanejado", e.target.value)}
                            />

                            <Input
                                label="Volume Realizado"
                                type="number"
                                value={registro.volumeRealizado ?? ""}
                                onChange={(e) => alterarCampo("volumeRealizado", e.target.value)}
                            />

                            <Input
                                label="Total Eventos em Aberto"
                                type="number"
                                value={registro.totalEventosAberto ?? ""}
                                onChange={(e) => alterarCampo("totalEventosAberto", e.target.value)}
                                disabled
                            />

                            <Input
                                label="GPS"
                                value={registro.gps ?? ""}
                                onChange={(e) => alterarCampo("gps", e.target.value)}
                            />

                            <Input
                                label="Imagens"
                                value={registro.imagens ?? ""}
                                onChange={(e) => alterarCampo("imagens", e.target.value)}
                            />

                        </div>
                    )}
                </Modal>
            </main>
        </div>
    );
}
